import { FC, useEffect, useState } from "react";
import { useLocalStorageChange } from "../../../system/hooks/useLocalStorageChange";
import { IJournalFilters, typeReport } from "../DashboardBusinessControls/interfaces";
import { useTranslation } from "react-i18next";
import { Accordion } from "../../../components/controls/accordion";
import { v4 as uuidv4} from 'uuid';
import styles from "../AnalysisLot/Styles/AnalysisFilterLot.module.scss"
import FlexColumn from "../../../components/controls/FlexColumn";
import { NumberInput } from "../../../components/controls/inputs/BaseInput";
import { Checkbox } from 'antd';

interface Props {
    onOpenAccordion?: (name: TypeAccordionName) => void;
    setFilters:(filters: IAnalysisFilters) => void;
    onFiltersLoaded: () => void;
    currentAccordion?: TypeAccordionName;
    tabId?: string;    
}

export type TypeAccordionName = 'mandatoryAssortment' | 'none'

export interface IAnalysisFilters {    
    IncludeReserve?: boolean,
    Threshold?: number,
}

export const defaultAnalysisFilters = {
    IncludeReserve: true,
    Threshold: 0
} as IAnalysisFilters

export const AnalyticalFilters: FC<Props> = ({
    onOpenAccordion,
    setFilters,
    onFiltersLoaded,
    tabId
}) => {
    const [activeAccordion, setActiveAccordion] = useState<TypeAccordionName>('none');
    const [savedFilters, setSavedFilters] = useLocalStorageChange<IJournalFilters>('journalFilters');
    const [analysisFilters, setAnalysisFilters] = useState<IAnalysisFilters>(defaultAnalysisFilters);

    const { t } = useTranslation();
    const baseT = (value: string) => t('analysisLot.' + value)

    const processFilters = (filters: IJournalFilters) => {
        try {
            const updatedFilters = { ...defaultAnalysisFilters };
            let accordionToOpen: TypeAccordionName = 'none';
            
            if (filters.reportType === typeReport.MandatoryAssortmentReport) {
                updatedFilters.IncludeReserve = filters.filters.DashboardMandatoryAssortment?.IncludeReserve || true
                updatedFilters.Threshold = filters.filters.DashboardMandatoryAssortment?.Threshold || 0

                accordionToOpen = 'mandatoryAssortment';
                setActiveAccordion('mandatoryAssortment');
            }

            setAnalysisFilters(updatedFilters);
            setFilters(updatedFilters);
            onOpenAccordion?.(accordionToOpen);
            setSavedFilters(null);
            localStorage.removeItem('journalFilters');
            onFiltersLoaded();
        } catch (error) {
            console.error(error);
        }
    };

    const handleAccordionToggle = (name: TypeAccordionName, isOpen: boolean) => {
        setActiveAccordion(isOpen ? name : 'none');
        onOpenAccordion?.(isOpen ? name : 'none');
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setFilters(analysisFilters);
        }, 200);
        return () => clearTimeout(timer);
    }, [analysisFilters]);

    useEffect(() => {
        if (!savedFilters || !tabId) return;

        if (savedFilters.tabId === tabId) {
            processFilters(savedFilters);
            setSavedFilters(null);
        }
    }, [savedFilters, tabId]);

    return <>
        <h1>{baseT('analysisMandatoryAssortmentLot')}</h1>
         <Accordion 
            opened={activeAccordion === 'mandatoryAssortment'}
            caption={baseT('mandatoryAssortmentFilters')} 
            id={`${uuidv4()}`}
            onOpen={(isOpen) => handleAccordionToggle('mandatoryAssortment', isOpen)}
        >
            <FlexColumn className={styles.flexColumn}>
                 <NumberInput
                    className={styles.numberInput} label={baseT('threshold')}
                    value={analysisFilters.Threshold}
                    min={0} 
                    onChange={(value) => setAnalysisFilters({...analysisFilters, Threshold: value})}
                />
                <div>
                    <Checkbox
                        name="includeReserve"
                        style={{ marginLeft: 0 }}
                        className={styles.customCheckbox}
                        checked={analysisFilters.IncludeReserve}
                        onChange={() => {
                           setAnalysisFilters({...analysisFilters, IncludeReserve: !analysisFilters.IncludeReserve})
                        }}
                        disabled={false}
                    />
                    {baseT('includeReserve')}
                </div>
            </FlexColumn>
        </Accordion>
    </>
}